/* Chair Caps Australia journey measurement v2.0.0 - 2026-08-22 */
(() => {
  'use strict';

  if (window.__ccaJourneyMeasurementV1) return;
  window.__ccaJourneyMeasurementV1 = true;

  const VERSION = '2.0.0';
  const TRACKER_SCHEMA_VERSION = '2';
  const TRACKER_BUILD_ID = 'cca_journey_260822_4';
  const COMMERCE_BUILD_ID = 'ai73_cart_260822_4';
  const DEPLOYMENT_ID = 'ai73_260822_d4';
  const SESSION_KEY = 'cca_journey_session_v1';
  const ACTION_KEY = 'cca_journey_last_action_v1';
  const COMMERCE_PENDING_KEY = 'cca_commerce_pending_v2';
  const COMMERCE_FAILURE_KEY = 'cca_commerce_failure_v2';
  const QA_SESSION_KEY = 'cca_measurement_qa_session_v1';
  const PERFORMANCE_SAMPLE_KEY = 'cca_performance_sample_v1';
  const SESSION_TTL_MS = 30 * 60 * 1000;
  const COMMERCE_TTL_MS = 2 * 60 * 1000;
  const RECOVERY_TTL_MS = 10 * 60 * 1000;
  const PERFORMANCE_SAMPLE_RATE = 10;
  const DEBUG = new URLSearchParams(location.search).get('cca_measurement_debug') === '1';
  const QA_QUERY = new URLSearchParams(location.search).get('cca_measurement_qa') === '1';
  if (QA_QUERY) {
    try { sessionStorage.setItem(QA_SESSION_KEY, 'true'); } catch (_) {}
  }
  const LIVE_QA = QA_QUERY || (() => {
    try { return sessionStorage.getItem(QA_SESSION_KEY) === 'true'; } catch (_) { return false; }
  })();
  const pendingGa = [];
  const pendingClarity = [];
  const seen = new Set();
  const counts = new Map();
  const lastHelpAt = new WeakMap();
  const debugEvents = [];
  const commerceActions = new Map();
  const performanceState = {
    sampled: false,
    lcp: 0,
    cls: 0,
    inp: 0,
    longTaskCount: 0,
    longTaskTotal: 0,
    supported: {},
    emitted: false,
  };
  let transportRetry;
  let clarityTransportRetry;
  let consentReadyRetry;
  let consentReadyAttempt = 0;
  let consentBoundInitialized = false;
  let performanceInitialized = false;
  let networkTransition = 'none';
  let networkTransitionAt = 0;
  let checkoutAttempt;

  const clean = (value, max = 100) => String(value ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);

  const productFamily = (handle = '') => {
    const value = clean(handle, 120).toLowerCase();
    if (value === 'groovy-2-0-floor-protector') return 'round';
    if (value === 'square-silicone-felt-chair-glides') return 'square';
    if (value === 'rectangular-silicone-felt-chair-glides') return 'rectangular';
    if (value === 'oval-silicone-felt-chair-glides') return 'oval';
    if (value === 'groovy-silicone-felt-glide') return 'groovy_flex';
    if (value === 'new-elite-groovy') return 'elite';
    if (value.includes('sled')) return 'sled';
    if (value.includes('gecko')) return 'gecko';
    if (value.includes('silicone')) return 'other_silicone';
    return value ? 'other' : 'not_applicable';
  };

  const pageFamily = (() => {
    if (location.pathname === '/') return 'homepage';
    if (location.pathname.startsWith('/products/')) return 'product';
    if (location.pathname.startsWith('/collections/')) return 'collection';
    if (location.pathname.startsWith('/cart')) return 'cart';
    if (location.pathname.startsWith('/search')) return 'search';
    return 'other';
  })();

  const productMeta = () => window.ShopifyAnalytics?.meta?.product || null;
  const currentHandle = () => productMeta()?.handle || location.pathname.match(/^\/products\/([^/?#]+)/)?.[1] || '';
  const currentFamily = () => productFamily(currentHandle());
  const currentProductId = () => clean(productMeta()?.id || document.querySelector('form[action*="/cart/add"] input[name="product-id"]')?.value, 40);
  const currentVariantId = () => clean(document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value || productMeta()?.variants?.[0]?.id, 40);
  const themeVersion = () => clean(window.Shopify?.theme?.id || 'unknown', 30);

  const state = {
    measurement_version: VERSION,
    tracker_schema_version: TRACKER_SCHEMA_VERSION,
    tracker_build_id: TRACKER_BUILD_ID,
    commerce_build_id: COMMERCE_BUILD_ID,
    deployment_id: DEPLOYMENT_ID,
    theme_version: themeVersion(),
    page_state_id: `${pageFamily}_v20260813`,
    homepage_merch_state: pageFamily === 'homepage' ? 'round_square_rect_v20260812' : 'not_applicable',
    collection_order_state: location.pathname.includes('/collections/silicone-chair-leg-protection') ? 'round_square_rect_v20260812' : 'not_applicable',
    selector_version: pageFamily === 'product' ? 'cca_selector_20260811' : 'not_applicable',
    offer_state: 'bau_aug2026',
    experiment_id: 'none',
    experiment_variant: 'none',
  };

  const analyticsAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed ? privacy.analyticsProcessingAllowed() === true : false;
    } catch (_) {
      return false;
    }
  };

  const randomRef = () => {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const journey = () => {
    if (!analyticsAllowed()) return null;
    const now = Date.now();
    try {
      const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
      if (stored?.ref && now - Number(stored.last || 0) <= SESSION_TTL_MS) {
        stored.last = now;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));
        return stored.ref;
      }
    } catch (_) {}
    const created = { ref: `cca_${randomRef()}`, last: now };
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(created)); } catch (_) {}
    return created.ref;
  };

  const context = () => ({
    ...state,
    product_family: currentFamily(),
    journey_ref: journey() || undefined,
    ...(LIVE_QA ? { debug_mode: true, cca_qa: 'true' } : {}),
  });

  const rememberAction = (action) => {
    const value = {
      action: clean(action, 40),
      at: Date.now(),
      page_family: pageFamily,
      product_family: currentFamily(),
    };
    try { sessionStorage.setItem(ACTION_KEY, JSON.stringify(value)); } catch (_) {}
    return value;
  };

  const recentAction = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(ACTION_KEY) || 'null');
      const age = Date.now() - Number(value?.at || 0);
      if (!value?.action || age < 0 || age > 30 * 60 * 1000) throw new Error('stale');
      const actionAgeBand = age <= 2000 ? '0_2s' : age <= 10000 ? '3_10s' : age <= 30000 ? '11_30s' : age <= 120000 ? '31_120s' : 'older';
      return {
        last_action: clean(value.action, 40),
        action_age_band: actionAgeBand,
        action_page_family: clean(value.page_family, 30),
        action_product_family: clean(value.product_family, 40),
      };
    } catch (_) {
      return { last_action: 'none', action_age_band: 'none', action_page_family: 'none', action_product_family: 'none' };
    }
  };

  const recordDebug = (destination, action, payload) => {
    debugEvents.push({ destination, action, payload, at: Date.now() });
  };

  const flushGa = () => {
    if (navigator.onLine === false || typeof window.gtag !== 'function' || !analyticsAllowed()) return false;
    while (pendingGa.length) {
      const [name, params] = pendingGa.shift();
      window.gtag('event', name, params);
    }
    return true;
  };

  const scheduleTransportRetry = () => {
    if (transportRetry || navigator.onLine === false) return;
    let attempt = 0;
    const retry = () => {
      transportRetry = null;
      if (flushGa() || attempt >= 4) return;
      attempt += 1;
      transportRetry = window.setTimeout(retry, [250, 750, 1500, 3000][attempt - 1]);
    };
    transportRetry = window.setTimeout(retry, 0);
  };

  const ga = (name, params = {}, includeContext = true) => {
    if (!analyticsAllowed()) return;
    const payload = includeContext ? { ...context(), ...params } : params;
    if (DEBUG) return recordDebug('ga4', name, payload);
    pendingGa.push([name, payload]);
    if (!flushGa()) scheduleTransportRetry();
  };

  const flushClarity = () => {
    if (navigator.onLine === false || typeof window.clarity !== 'function' || !analyticsAllowed()) return false;
    while (pendingClarity.length) {
      const [action, args] = pendingClarity.shift();
      window.clarity(action, ...args);
    }
    return true;
  };

  const scheduleClarityRetry = () => {
    if (clarityTransportRetry || navigator.onLine === false) return;
    let attempt = 0;
    const retry = () => {
      clarityTransportRetry = null;
      if (flushClarity() || attempt >= 4) return;
      attempt += 1;
      clarityTransportRetry = window.setTimeout(retry, [250, 750, 1500, 3000][attempt - 1]);
    };
    clarityTransportRetry = window.setTimeout(retry, 0);
  };

  const clarity = (action, ...args) => {
    if (!analyticsAllowed()) return;
    if (DEBUG) return recordDebug('clarity', action, args);
    pendingClarity.push([action, args]);
    if (!flushClarity()) scheduleClarityRetry();
  };

  const clarityEvent = (name) => clarity('event', name);
  const clarityTag = (key, value) => clarity('set', key, clean(value, 120));

  const readJson = (key, fallback) => {
    try { return JSON.parse(sessionStorage.getItem(key) || 'null') ?? fallback; } catch (_) { return fallback; }
  };

  const writeJson = (key, value) => {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };

  const actionId = () => `act_${randomRef()}`;

  const elapsedBand = (milliseconds) => {
    const value = Number(milliseconds || 0);
    if (value < 250) return 'under_250ms';
    if (value < 750) return '250_749ms';
    if (value < 1500) return '750_1499ms';
    if (value < 3000) return '1500_2999ms';
    if (value < 8000) return '3_7s';
    if (value < 15000) return '8_14s';
    if (value < 30000) return '15_29s';
    return '30s_plus';
  };

  const metricBand = (value, thresholds, labels) => {
    if (!Number.isFinite(value) || value <= 0) return 'not_observed';
    const index = thresholds.findIndex((threshold) => value <= threshold);
    return labels[index < 0 ? labels.length - 1 : index];
  };

  const performanceContext = () => {
    if (!performanceState.sampled) return { performance_sample: 'excluded' };
    return {
      performance_sample: 'included',
      lcp_band: performanceState.supported.lcp ? metricBand(performanceState.lcp, [1500, 2500, 4000], ['under_1_5s', '1_5_2_5s', '2_5_4s', 'over_4s']) : 'unsupported',
      cls_band: performanceState.supported.cls ? (performanceState.cls <= 0.05 ? 'under_0_05' : performanceState.cls <= 0.1 ? '0_05_0_1' : performanceState.cls <= 0.25 ? '0_1_0_25' : 'over_0_25') : 'unsupported',
      inp_band: performanceState.supported.inp ? metricBand(performanceState.inp, [100, 200, 500], ['under_100ms', '100_200ms', '200_500ms', 'over_500ms']) : 'unsupported',
      long_task_count_band: performanceState.supported.longtask ? (performanceState.longTaskCount === 0 ? '0' : performanceState.longTaskCount <= 2 ? '1_2' : performanceState.longTaskCount <= 5 ? '3_5' : '6_plus') : 'unsupported',
      long_task_time_band: performanceState.supported.longtask ? (performanceState.longTaskTotal === 0 ? '0' : metricBand(performanceState.longTaskTotal, [100, 300, 800], ['under_100ms', '100_300ms', '300_800ms', 'over_800ms'])) : 'unsupported',
    };
  };

  const responseContext = (response) => {
    const contentType = clean(response?.headers?.get?.('content-type') || '', 80).toLowerCase();
    const challenge = clean(response?.headers?.get?.('cf-mitigated') || '', 30).toLowerCase();
    const status = Number(response?.status || 0);
    return {
      http_status_family: status >= 500 ? '5xx' : status >= 400 ? '4xx' : status >= 300 ? '3xx' : status >= 200 ? '2xx' : status ? 'other' : 'unknown',
      content_family: /json|javascript/.test(contentType) ? 'json' : contentType.includes('html') ? 'html' : contentType ? 'other' : 'unknown',
      challenge_signal: challenge === 'challenge' ? 'cf_mitigated' : 'none',
    };
  };

  const networkContext = () => ({
    online_state: navigator.onLine === false ? 'offline' : 'online',
    network_transition: Date.now() - networkTransitionAt <= 30000 ? networkTransition : 'none',
    visibility_state: document.visibilityState === 'hidden' ? 'hidden' : 'visible',
  });

  const safeCommerceFields = (fields = {}) => ({
    surface: clean(fields.surface || 'unknown', 30),
    submit_mode: clean(fields.submit_mode || 'unknown', 30),
    transport_owner: clean(fields.transport_owner || 'unknown', 40),
    hydration_state: clean(fields.hydration_state || 'unknown', 30),
    result_family: clean(fields.result_family || 'none', 50),
    http_status_family: clean(fields.http_status_family || 'unknown', 20),
    content_family: clean(fields.content_family || 'unknown', 20),
    challenge_signal: clean(fields.challenge_signal || 'unknown', 20),
    product_id: clean(fields.product_id || '', 40) || undefined,
    variant_id: clean(fields.variant_id || '', 40) || undefined,
    recovery_source: clean(fields.recovery_source || 'none', 40),
    handoff_family: clean(fields.handoff_family || 'none', 40),
    navigation_type: clean(fields.navigation_type || 'none', 30),
  });

  const pendingActions = () => {
    const value = readJson(COMMERCE_PENDING_KEY, []);
    return Array.isArray(value) ? value.filter((entry) => entry?.action_id && Date.now() - Number(entry.at || 0) <= COMMERCE_TTL_MS).slice(-3) : [];
  };

  const persistCommerce = (action) => {
    const pending = pendingActions().filter((entry) => entry.action_id !== action.action_id);
    pending.push({
      action_id: action.action_id,
      action_type: action.action_type,
      at: action.at,
      eligible: action.eligible,
      page_family: action.page_family,
      navigation_observed: Boolean(action.navigation_observed),
      ...safeCommerceFields(action),
    });
    writeJson(COMMERCE_PENDING_KEY, pending.slice(-3));
  };

  const removePendingCommerce = (id) => {
    const pending = pendingActions().filter((entry) => entry.action_id !== id);
    if (pending.length) writeJson(COMMERCE_PENDING_KEY, pending);
    else {
      try { sessionStorage.removeItem(COMMERCE_PENDING_KEY); } catch (_) {}
    }
  };

  const emitCommerce = (action, phase, fields = {}) => {
    if (!action?.eligible) return;
    const safe = safeCommerceFields({ ...action, ...action.response, ...fields });
    const network = networkContext();
    const payload = {
      tracker_schema_version: TRACKER_SCHEMA_VERSION,
      tracker_build_id: TRACKER_BUILD_ID,
      commerce_build_id: COMMERCE_BUILD_ID,
      deployment_id: DEPLOYMENT_ID,
      theme_version: themeVersion(),
      page_family: clean(action.page_family || pageFamily, 30),
      journey_ref: journey() || undefined,
      action_id: clean(action.action_id, 32),
      action_type: clean(action.action_type, 20),
      action_phase: clean(phase, 30),
      elapsed_band: elapsedBand(Date.now() - action.at),
      surface: safe.surface,
      submit_mode: safe.submit_mode,
      transport_owner: safe.transport_owner,
      hydration_state: safe.hydration_state,
      result_family: safe.result_family,
      online_state: network.online_state,
      visibility_state: network.visibility_state,
      environment_family: environmentFamily(),
      recovery_source: safe.recovery_source,
      handoff_family: safe.handoff_family,
    };
    [
      ['variant_id', safe.variant_id],
      ['http_status_family', safe.http_status_family],
      ['content_family', safe.content_family],
      ['challenge_signal', safe.challenge_signal],
      ['network_transition', network.network_transition],
      ['navigation_type', safe.navigation_type],
    ].forEach(([key, value]) => {
      if (value && value !== 'unknown' && value !== 'none' && Object.keys(payload).length < 25) payload[key] = value;
    });
    ga('cca_commerce_action', payload, false);
    clarityTag('cca_commerce_action', payload.action_type);
    clarityTag('cca_commerce_phase', payload.action_phase);
    clarityTag('cca_commerce_result', payload.result_family);
    clarityTag('cca_commerce_build', payload.commerce_build_id);
    clarityEvent('cca_commerce_action');
  };

  const beginCommerce = (type, fields = {}) => {
    const id = clean(fields.action_id || actionId(), 32);
    const existing = commerceActions.get(id);
    if (existing) {
      Object.assign(existing, safeCommerceFields({ ...existing, ...fields }));
      persistCommerce(existing);
      return id;
    }
    const action = {
      action_id: id,
      action_type: clean(type, 20),
      at: Date.now(),
      eligible: analyticsAllowed(),
      page_family: pageFamily,
      phases: new Set(['attempt']),
      terminal: false,
      wasHidden: document.visibilityState === 'hidden',
      ...safeCommerceFields(fields),
    };
    commerceActions.set(id, action);
    persistCommerce(action);
    rememberAction(`${action.action_type}_attempt`);
    emitCommerce(action, 'attempt');
    const timeout = action.action_type === 'checkout' ? 20000 : 15000;
    action.timer = window.setTimeout(() => {
      if (action.terminal || action.wasHidden || document.visibilityState !== 'visible' || navigator.onLine === false) return;
      finishCommerce(id, 'unresolved_interrupted', { result_family: 'timeout_visible_online' });
    }, timeout);
    return id;
  };

  const observeCommerceResponse = (id, response) => {
    const action = commerceActions.get(clean(id, 32));
    const detail = responseContext(response);
    if (action) {
      action.response = detail;
      persistCommerce(action);
    }
    return detail;
  };

  const finishCommerce = (id, phase, fields = {}) => {
    const key = clean(id, 32);
    const action = commerceActions.get(key);
    if (!action) return false;
    const boundedPhase = clean(phase, 30);
    if (action.phases.has(boundedPhase)) return false;
    const terminal = ['confirmed', 'user_error', 'request_error', 'unresolved_interrupted'].includes(boundedPhase);
    if (action.terminal && boundedPhase !== 'recovered') return false;
    action.phases.add(boundedPhase);
    if (boundedPhase === 'navigation_observed') action.navigation_observed = true;
    Object.assign(action, safeCommerceFields({ ...action, ...fields }));
    emitCommerce(action, boundedPhase, fields);
    if (!terminal) {
      persistCommerce(action);
      return true;
    }
    action.terminal = true;
    if (action.timer) window.clearTimeout(action.timer);
    removePendingCommerce(key);
    if (boundedPhase === 'user_error' || boundedPhase === 'request_error' || boundedPhase === 'unresolved_interrupted') {
      writeJson(COMMERCE_FAILURE_KEY, {
        action_type: action.action_type,
        at: Date.now(),
        result_family: clean(fields.result_family || boundedPhase, 50),
      });
    } else if (boundedPhase === 'confirmed') {
      const failure = readJson(COMMERCE_FAILURE_KEY, null);
      if (failure?.action_type === action.action_type && Date.now() - Number(failure.at || 0) <= RECOVERY_TTL_MS) {
        emitCommerce(action, 'recovered', { recovery_source: 'retry_after_failure', result_family: 'confirmed_after_failure' });
        try { sessionStorage.removeItem(COMMERCE_FAILURE_KEY); } catch (_) {}
      }
    }
    window.setTimeout(() => commerceActions.delete(key), 30000);
    return true;
  };

  const handoffCommerce = (id, fields = {}) => finishCommerce(id, 'navigation_observed', fields);

  const cartContainsVariant = (variantId) => {
    const value = clean(variantId, 40);
    if (!/^\d+$/.test(value)) return false;
    return Boolean(document.querySelector(`[data-cart-variant-id="${value}"], [data-quantity-variant-id="${value}"]`));
  };

  const navigationType = () => clean(performance.getEntriesByType?.('navigation')?.[0]?.type || 'unknown', 30);

  const initPendingCommerce = () => {
    const type = navigationType();
    pendingActions().forEach((stored) => {
      if (commerceActions.has(stored.action_id)) return;
      const action = {
        ...stored,
        phases: new Set(['attempt']),
        terminal: false,
        wasHidden: false,
      };
      commerceActions.set(action.action_id, action);
      const age = Date.now() - Number(action.at || 0);
      if (action.action_type === 'add' && pageFamily === 'cart' && cartContainsVariant(action.variant_id)) {
        finishCommerce(action.action_id, 'confirmed', {
          result_family: 'server_rendered_cart_contains_variant',
          recovery_source: 'next_document',
          navigation_type: type,
        });
      } else if (age <= 30000 && type === 'reload' && pageFamily === action.page_family && navigator.onLine !== false) {
        finishCommerce(action.action_id, 'unresolved_interrupted', {
          result_family: `${action.action_type}_reload_before_confirmation`,
          navigation_type: type,
        });
      } else if (!action.navigation_observed) {
        handoffCommerce(action.action_id, {
          result_family: 'next_document_observed',
          navigation_type: type,
        });
      }
    });
  };

  const initCommerceLifecycle = () => {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        commerceActions.forEach((action) => { if (!action.terminal) action.wasHidden = true; });
      }
    }, { passive: true });
    window.addEventListener('pagehide', () => {
      commerceActions.forEach((action) => {
        if (!action.terminal) handoffCommerce(action.action_id, {
          result_family: 'document_hidden_or_unloaded',
          navigation_type: navigationType(),
        });
      });
    }, { passive: true });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      commerceActions.forEach((action) => {
        if (!action.terminal) handoffCommerce(action.action_id, {
          result_family: 'bfcache_restore',
          navigation_type: 'back_forward',
        });
      });
    }, { passive: true });
    initPendingCommerce();
  };

  const initPerformanceDiagnostics = () => {
    if (performanceInitialized || !analyticsAllowed() || !('PerformanceObserver' in window)) return;
    performanceInitialized = true;
    const ref = journey();
    if (!ref) return;
    let sampled;
    try {
      const stored = sessionStorage.getItem(PERFORMANCE_SAMPLE_KEY);
      sampled = stored === 'true' ? true : stored === 'false' ? false : undefined;
    } catch (_) {}
    if (typeof sampled !== 'boolean') {
      const bucket = [...ref].reduce((sum, character) => (sum + character.charCodeAt(0)) % 100, 0);
      sampled = bucket < PERFORMANCE_SAMPLE_RATE;
      try { sessionStorage.setItem(PERFORMANCE_SAMPLE_KEY, String(sampled)); } catch (_) {}
    }
    performanceState.sampled = sampled;
    if (!sampled) return;
    const supported = window.PerformanceObserver.supportedEntryTypes || [];
    performanceState.supported = {
      lcp: supported.includes('largest-contentful-paint'),
      cls: supported.includes('layout-shift'),
      inp: supported.includes('event'),
      longtask: supported.includes('longtask'),
    };
    const observe = (type, callback) => {
      if (!supported.includes(type)) return;
      try { new PerformanceObserver((list) => callback(list.getEntries())).observe({ type, buffered: true }); } catch (_) {}
    };
    observe('largest-contentful-paint', (entries) => {
      const last = entries[entries.length - 1];
      if (last) performanceState.lcp = Math.max(performanceState.lcp, last.startTime || 0);
    });
    observe('layout-shift', (entries) => {
      entries.forEach((entry) => { if (!entry.hadRecentInput) performanceState.cls += Number(entry.value || 0); });
    });
    observe('event', (entries) => {
      entries.forEach((entry) => { if (entry.interactionId) performanceState.inp = Math.max(performanceState.inp, Number(entry.duration || 0)); });
    });
    observe('longtask', (entries) => {
      performanceState.longTaskCount += entries.length;
      entries.forEach((entry) => { performanceState.longTaskTotal += Number(entry.duration || 0); });
    });
    const emitPerformance = () => {
      if (performanceState.emitted || !analyticsAllowed()) return;
      performanceState.emitted = true;
      ga('cca_field_performance', { sample_rate: '10_percent', ...performanceContext() });
    };
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') emitPerformance(); }, { passive: true });
    window.addEventListener('pagehide', emitPerformance, { passive: true });
  };

  const identify = () => {
    const ref = journey();
    if (!ref) return;
    clarity('identify', ref, ref, state.page_state_id);
    clarityTag('cca_page_state', state.page_state_id);
    clarityTag('cca_product_family', currentFamily());
    clarityTag('cca_selector_version', state.selector_version);
    clarityTag('cca_offer_state', state.offer_state);
    clarityTag('cca_experiment', `${state.experiment_id}:${state.experiment_variant}`);
    if (LIVE_QA) clarityTag('cca_qa', 'true');
  };

  const cappedIndex = (key) => {
    const next = (counts.get(key) || 0) + 1;
    counts.set(key, next);
    return next === 1 ? '1' : next === 2 ? '2' : '3_plus';
  };

  const itemFromCurrentProduct = () => {
    const meta = productMeta();
    return {
      item_id: currentProductId(),
      item_name: clean(meta?.variants?.[0]?.name?.split(' - ')[0] || document.querySelector('h1')?.textContent, 140),
      item_variant: currentVariantId(),
      item_category: currentFamily(),
    };
  };

  const parseCard = (link) => {
    const href = link?.getAttribute('href') || '';
    const handle = href.match(/\/products\/([^/?#]+)/)?.[1] || '';
    const idMatch = link?.id?.match(/-(\d+)$/);
    const root = link?.closest('.card-wrapper, .product-card-wrapper, li, article');
    const section = link?.closest('.shopify-section, section');
    const uniqueLinks = section ? [...section.querySelectorAll('a[id^="CardLink-"][href*="/products/"]')] : [];
    const unique = uniqueLinks.filter((candidate, index, all) => all.findIndex((other) => other.href === candidate.href) === index);
    const sectionKey = link?.id?.match(/^CardLink-(.+)-\d+$/)?.[1]?.split('__').pop() || section?.id || 'product_list';
    return {
      link,
      root,
      section,
      handle,
      family: productFamily(handle),
      item: {
        item_id: idMatch?.[1] || clean(handle, 80),
        item_name: clean(link?.textContent, 140),
        item_category: productFamily(handle),
        item_list_id: clean(sectionKey, 90),
        item_list_name: clean(section?.querySelector('h1, h2, h3')?.textContent || sectionKey, 120),
        index: Math.max(1, unique.findIndex((candidate) => candidate.href === link.href) + 1),
      },
    };
  };

  const initLists = () => {
    if (!('IntersectionObserver' in window)) return;
    const links = [...document.querySelectorAll('a[id^="CardLink-"][href*="/products/"]')];
    const unique = links.filter((link, index, all) => all.findIndex((other) => other.href === link.href && other.closest('.shopify-section, section') === link.closest('.shopify-section, section')) === index);
    const targetLinks = new WeakMap();
    const observer = new IntersectionObserver((entries) => {
      const batches = new Map();
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        observer.unobserve(entry.target);
        const card = parseCard(targetLinks.get(entry.target) || entry.target);
        if (!card.item.item_id) continue;
        const key = `${card.item.item_list_id}:${card.item.item_id}`;
        if (seen.has(`list:${key}`)) continue;
        seen.add(`list:${key}`);
        if (!batches.has(card.item.item_list_id)) batches.set(card.item.item_list_id, []);
        batches.get(card.item.item_list_id).push(card.item);
      }
      for (const items of batches.values()) {
        if (items.length) ga('view_item_list', { item_list_id: items[0].item_list_id, item_list_name: items[0].item_list_name, items });
      }
    }, { threshold: [0.5] });
    unique.forEach((link) => {
      const target = link.closest('.card-wrapper, .product-card-wrapper') || link;
      targetLinks.set(target, link);
      observer.observe(target);
    });
  };

  const initPromotion = () => {
    const hero = document.querySelector('.cca-home-hero');
    const button = hero?.querySelector('.cca-home-hero__button');
    if (!hero || !button || !('IntersectionObserver' in window)) return;
    const promotion = { promotion_id: 'cca_home_hero_aug2026', promotion_name: 'shop_chair_caps', creative_name: 'move_chairs_without_scratches', creative_slot: 'homepage_hero' };
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
        observer.disconnect();
        ga('view_promotion', { items: [promotion] });
      }
    }, { threshold: [0.5] });
    observer.observe(hero);
    button.addEventListener('click', () => {
      rememberAction('select_promotion');
      ga('select_promotion', { items: [promotion] });
    }, { passive: true });
  };

  const optionType = (input) => {
    const raw = clean(input?.name || input?.closest('fieldset')?.querySelector('legend')?.textContent, 50).toLowerCase();
    if (raw.includes('size')) return 'size';
    if (raw.includes('pack') || raw.includes('quantity')) return 'pack';
    if (raw.includes('colour') || raw.includes('color')) return 'colour';
    if (raw.includes('shape')) return 'shape';
    return 'other';
  };

  const issueForOption = (input) => {
    const label = input?.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
    if (input?.disabled || input?.classList.contains('disabled') || label?.classList.contains('disabled') || input?.getAttribute('aria-disabled') === 'true') return 'unavailable_option';
    return '';
  };

  const sendSelectionIssue = (input, issueType) => {
    const key = `${currentProductId()}:${optionType(input)}:${clean(input?.value, 60)}:${issueType}`;
    if (seen.has(`issue:${key}`)) return;
    seen.add(`issue:${key}`);
    rememberAction('selection_issue');
    ga('cca_selection_issue', {
      issue_type: issueType,
      option_type: optionType(input),
      option_value: clean(input?.value, 60),
      recoverable: 'true',
      items: [itemFromCurrentProduct()],
    });
    clarityTag('cca_selection_issue', issueType);
    clarityEvent('cca_selection_issue');
  };

  const initOptions = () => {
    document.addEventListener('click', (event) => {
      const label = event.target.closest('label[for]');
      const input = event.target.matches?.('variant-selects input') ? event.target : (label ? document.getElementById(label.htmlFor) : null);
      if (!input?.closest('variant-selects')) return;
      const issue = issueForOption(input);
      if (issue) sendSelectionIssue(input, issue);
    }, true);

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input?.matches?.('variant-selects input, variant-selects select')) return;
      const type = optionType(input);
      rememberAction('option_change');
      ga('cca_option_change', {
        option_type: type,
        option_value: clean(input.value, 60),
        change_index: cappedIndex(`option:${type}`),
        items: [itemFromCurrentProduct()],
      });
      clarityTag('cca_last_option_type', type);
      clarityEvent('cca_option_change');
      if (issueForOption(input)) return;
      window.setTimeout(() => {
        if (input.matches('input[type="radio"]') && !input.checked) return;
        const submit = document.querySelector('form[data-type="add-to-cart-form"] [type="submit"]');
        if (submit?.disabled && /sold out|unavailable/i.test(submit.textContent || '')) sendSelectionIssue(input, /sold out/i.test(submit.textContent || '') ? 'sold_out_variant' : 'invalid_combination');
      }, 100);
    }, true);
  };

  const helpType = (element) => {
    const text = clean(element?.textContent || element?.getAttribute?.('aria-label'), 180).toLowerCase();
    if (/measure|size|fit/.test(text)) return 'measurement_fit';
    if (/pack|pieces|quantity/.test(text)) return 'pack';
    if (/shipping|delivery|dispatch/.test(text)) return 'shipping';
    if (/return|refund|guarantee|money-back/.test(text)) return 'returns_guarantee';
    if (/chat|help|contact/.test(text)) return 'contact_help';
    return 'faq_other';
  };

  const sendHelp = (element, placement) => {
    const now = Date.now();
    if (now - Number(lastHelpAt.get(element) || 0) < 1000) return;
    lastHelpAt.set(element, now);
    const type = helpType(element);
    rememberAction('help_open');
    ga('cca_help_open', { help_type: type, placement, items: currentProductId() ? [itemFromCurrentProduct()] : undefined });
    clarityTag('cca_help_type', type);
    clarityEvent('cca_help_open');
  };

  const initHelp = () => {
    document.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!details?.matches?.('details') || !details.open) return;
      const summary = details.querySelector(':scope > summary');
      if (!summary || !/measure|fit|size|pack|pieces|help|shipping|delivery|return|guarantee|sample/i.test(summary.textContent || '')) return;
      sendHelp(summary, details.closest('product-info') ? 'pdp_accordion' : 'page_content');
    }, true);
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.htm-guide__video-button, .htm-guide__chat-button, .htm-guide__disclosure-button, [aria-controls*="Measure"], [aria-label*="measur" i]');
      if (button) sendHelp(button, 'measurement_guide');
    }, true);
  };

  const cartErrorText = () => [...document.querySelectorAll('.product-form__error-message, .cart-item__error-text, #cart-errors, [data-cart-error]')]
    .map((element) => clean(element.textContent, 160))
    .find(Boolean) || '';

  const sendCartError = (family = 'cart_add_failed') => {
    const signature = `${family}:${cartErrorText()}`;
    if (seen.has(`cart-error:${signature}`)) return;
    seen.add(`cart-error:${signature}`);
    ga('cca_add_to_cart_error', { error_family: family, recoverable: 'true', items: currentProductId() ? [itemFromCurrentProduct()] : undefined });
    clarityTag('cca_cart_error', family);
    clarityEvent('cca_add_to_cart_error');
  };

  const beginCheckout = (element, dynamic = false) => {
    const now = Date.now();
    if (checkoutAttempt && now - checkoutAttempt.at < 1500) return checkoutAttempt.id;
    const surface = dynamic
      ? 'dynamic_checkout'
      : element?.closest?.('cart-drawer')
        ? 'cart_drawer'
        : pageFamily === 'cart'
          ? 'cart_page'
          : 'other';
    const id = beginCommerce('checkout', {
      surface,
      submit_mode: 'document',
      transport_owner: dynamic ? 'shopify_dynamic_checkout' : 'shopify_checkout_form',
      hydration_state: 'ready',
      result_family: 'checkout_handoff_started',
    });
    checkoutAttempt = { id, at: now };
    return id;
  };

  const initCartErrors = () => {
    document.addEventListener('submit', (event) => {
      if (!event.target?.matches?.('form[action*="/cart/add"]')) return;
      rememberAction('add_to_cart_attempt');
      [1200, 4000].forEach((delay) => window.setTimeout(() => { if (cartErrorText()) sendCartError(); }, delay));
    }, true);
    document.addEventListener('click', (event) => {
      const checkout = event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
      const dynamic = event.target.closest('.shopify-payment-button, .cart__dynamic-checkout-buttons, .additional-checkout-buttons');
      if (checkout || dynamic) {
        rememberAction('checkout_attempt');
        beginCheckout(checkout || dynamic, Boolean(dynamic));
      }
    }, true);
    document.addEventListener('submit', (event) => {
      if (event.submitter?.matches?.('button[name="checkout"], input[name="checkout"]')) beginCheckout(event.submitter, false);
    }, true);
    document.querySelectorAll('.product-form__error-message, .cart-item__error-text, #cart-errors, [data-cart-error]').forEach((element) => {
      new MutationObserver(() => { if (cartErrorText()) sendCartError(); }).observe(element, { childList: true, characterData: true, subtree: true, attributes: true });
    });
  };

  const quantityBand = (value) => {
    const number = Number(value || 0);
    if (number <= 0) return '0';
    if (number === 1) return '1';
    if (number <= 3) return '2_3';
    if (number <= 7) return '4_7';
    return '8_plus';
  };

  const sendQuantity = (direction, from, to) => {
    const key = `${direction}:${quantityBand(from)}:${quantityBand(to)}`;
    const now = Date.now();
    if (window.__ccaLastQuantityEvent?.key === key && now - window.__ccaLastQuantityEvent.at < 1200) return;
    window.__ccaLastQuantityEvent = { key, at: now };
    rememberAction(`cart_${direction}`);
    ga('cca_cart_quantity_change', { direction, from_band: quantityBand(from), to_band: quantityBand(to) });
    clarityTag('cca_cart_edit', direction);
    clarityEvent('cca_cart_quantity_change');
  };

  const initCartQuantity = () => {
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('cart-remove-button a, cart-remove-button button, [data-cart-remove]');
      if (remove) return sendQuantity('remove', 1, 0);
      const button = event.target.closest('cart-drawer .quantity__button, cart-items .quantity__button');
      if (!button) return;
      const input = button.closest('.quantity')?.querySelector('input[type="number"]');
      const from = Number(input?.value || 0);
      window.setTimeout(() => {
        const to = Number(input?.value || 0);
        if (to !== from) sendQuantity(to > from ? 'increase' : 'decrease', from, to);
      }, 500);
    }, true);
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input?.matches?.('cart-drawer input[type="number"], cart-items input[type="number"]')) return;
      const from = Number(input.defaultValue || 0);
      const to = Number(input.value || 0);
      if (to !== from) sendQuantity(to > from ? 'increase' : to === 0 ? 'remove' : 'decrease', from, to);
    }, true);
  };

  const initProductSwitch = () => {
    if (pageFamily !== 'product') return;
    const family = currentFamily();
    try {
      const previous = sessionStorage.getItem('cca_last_product_family_v1');
      if (previous && previous !== family && previous !== 'not_applicable' && family !== 'not_applicable') {
        rememberAction('product_switch');
        ga('cca_product_switch', { from_family: clean(previous, 50), to_family: family, switch_index: cappedIndex('product_switch') });
        clarityTag('cca_product_switch', `${previous}_to_${family}`);
        clarityEvent('cca_product_switch');
      }
      sessionStorage.setItem('cca_last_product_family_v1', family);
    } catch (_) {}
  };

  const initSelectItem = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="/products/"]');
      if (!link) return;
      const card = parseCard(link);
      if (!card.item.item_id || !card.root) return;
      rememberAction('select_item');
      ga('select_item', { item_list_id: card.item.item_list_id, item_list_name: card.item.item_list_name, items: [card.item] });
      clarityTag('cca_selected_family', card.family);
      clarityEvent('cca_select_item');
    }, true);
  };

  const sourceFamily = (filename = '') => {
    if (!clean(filename, 500)) return 'unknown';
    try {
      const url = new URL(filename, location.href);
      if (url.hostname === location.hostname || /shopifycdn\.com$/.test(url.hostname)) return 'theme_asset';
      return 'app_or_third_party';
    } catch (_) { return 'unknown'; }
  };

  const environmentFamily = () => {
    const ua = navigator.userAgent || '';
    if (/FBAN|FBAV|Instagram/i.test(ua)) return 'meta_in_app';
    if (/Android/i.test(ua) && /; wv\)|\bwv\b/i.test(ua)) return 'android_webview';
    if (/CriOS/i.test(ua)) return 'ios_chrome';
    if (/FxiOS/i.test(ua)) return 'ios_firefox';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios_safari';
    if (/Android/i.test(ua)) return 'android_browser';
    return 'desktop_browser';
  };

  const errorFamily = (issueType, message = '') => {
    const value = clean(message, 300).toLowerCase();
    if (/_?autofillcallbackhandler|autofill.*callback/i.test(value)) return 'browser_autofill_bridge';
    if (/webkit\.messagehandlers|java object is gone|error invoking postmessage/i.test(value)) return 'browser_webview_bridge';
    if (/idbdatabase|object stores? was not found|indexeddb/i.test(value)) return 'browser_storage';
    if (/failed to fetch|networkerror|load failed/i.test(value)) return 'network_request_failure';
    if (issueType === 'resource_load_error') return 'resource_load_failure';
    if (issueType === 'unhandled_promise_rejection') return 'promise_rejection';
    if (/^script error\.?$/i.test(value)) return 'opaque_script_error';
    return 'javascript_runtime';
  };

  const serviceFamily = (filename = '', family = '') => {
    if (/^browser_/.test(family)) return 'browser_runtime';
    if (!clean(filename, 500)) return 'unknown';
    let url;
    try { url = new URL(filename, location.href); } catch (_) { return 'unknown'; }
    const host = url.hostname.toLowerCase();
    const path = url.pathname.toLowerCase();
    if (host === location.hostname && /\/cdn\/shop\/t\/\d+\/assets\//.test(path)) return 'theme_asset';
    if (/cdn\.shopify\.com|shopifycdn\.com/.test(host) && /\/s\/files\/.*\/assets\//.test(path)) return 'theme_asset';
    if (/clarity\.ms|clarity\.microsoft/.test(host)) return 'microsoft_clarity';
    if (/klaviyo/.test(host)) return 'klaviyo';
    if (/loox/.test(host)) return 'loox';
    if (/facebook|fbcdn|instagram|meta\.com/.test(host)) return 'meta_service';
    if (/google|gstatic|youtube|doubleclick/.test(host)) return 'google_service';
    if (/afterpay|klarna|paypal|stripe/.test(host)) return 'payment_messaging';
    if (/aov|qikify|cart|discount|bulkperk/.test(host + path)) return 'cart_app';
    if (/shopify|shop\.app/.test(host)) return 'shopify_platform';
    if (host === location.hostname) return 'storefront_asset';
    return host ? 'other_third_party' : 'unknown';
  };

  const technicalError = (issueType, resourceType, filename, message = '') => {
    const family = errorFamily(issueType, message);
    const service = serviceFamily(filename, family);
    const environment = environmentFamily();
    const signature = `${issueType}:${resourceType}:${family}:${service}`;
    if (seen.has(`technical:${signature}`)) return;
    if ((counts.get('technical_error') || 0) >= 3) return;
    seen.add(`technical:${signature}`);
    counts.set('technical_error', (counts.get('technical_error') || 0) + 1);
    ga('cca_technical_error', {
      issue_type: issueType,
      error_family: family,
      resource_type: resourceType,
      service_family: service,
      source_family: sourceFamily(filename),
      environment_family: environment,
      ...recentAction(),
      recoverable: 'unknown',
    });
    clarityTag('cca_technical_error', family);
    clarityTag('cca_error_service', service);
    clarityTag('cca_error_environment', environment);
    clarityTag('cca_error_last_action', recentAction().last_action);
    clarityEvent('cca_technical_error');
  };

  const initErrors = () => {
    window.addEventListener('error', (event) => {
      const target = event.target;
      if (target && target !== window && /^(SCRIPT|LINK|IMG|IFRAME|VIDEO|SOURCE)$/.test(target.tagName || '')) {
        technicalError('resource_load_error', target.tagName.toLowerCase(), target.src || target.href || '');
      } else if (event.message || event.filename) {
        technicalError('javascript_error', 'script', event.filename, event.message || event.error?.message || '');
      }
    }, true);
    window.addEventListener('unhandledrejection', (event) => {
      const message = typeof event.reason === 'string' ? event.reason : event.reason?.message || '';
      technicalError('unhandled_promise_rejection', 'script', '', message);
    }, { passive: true });
  };

  const initSearch = () => {
    if (pageFamily !== 'search') return;
    const raw = new URLSearchParams(location.search).get('q') || '';
    const looksSensitive = /@|\b\d{7,}\b/.test(raw);
    const term = looksSensitive ? '[redacted]' : clean(raw, 50).toLowerCase();
    if (term) ga('view_search_results', { search_term: term });
    const resultLinks = document.querySelectorAll('a[id^="CardLink-"][href*="/products/"]');
    if (term && resultLinks.length === 0) {
      ga('cca_selection_issue', { issue_type: 'zero_search_results', option_type: 'search', recoverable: 'true' });
      clarityTag('cca_selection_issue', 'zero_search_results');
      clarityEvent('cca_selection_issue');
    }
  };

  const initConsentBoundState = () => {
    if (consentBoundInitialized) return;
    if (!analyticsAllowed()) {
      if (consentReadyRetry || consentReadyAttempt >= 4) return;
      const delay = [250, 750, 1500, 3000][consentReadyAttempt];
      consentReadyAttempt += 1;
      consentReadyRetry = window.setTimeout(() => {
        consentReadyRetry = null;
        initConsentBoundState();
      }, delay);
      return;
    }
    consentBoundInitialized = true;
    drainCommerceQueue();
    identify();
    initPerformanceDiagnostics();
    initLists();
    initPromotion();
    initProductSwitch();
    initSearch();
  };

  const resumeTransports = () => {
    window.setTimeout(() => {
      if (pendingGa.length && !flushGa()) scheduleTransportRetry();
      if (pendingClarity.length && !flushClarity()) scheduleClarityRetry();
    }, 1000);
  };

  const markNetworkTransition = (type) => {
    networkTransition = clean(type, 20);
    networkTransitionAt = Date.now();
    if (type === 'offline') {
      commerceActions.forEach((action) => { if (!action.terminal) action.wasOffline = true; });
    }
  };

  const drainCommerceQueue = () => {
    const queue = Array.isArray(window.__ccaCommerceDiagnosticQueue) ? window.__ccaCommerceDiagnosticQueue.splice(0) : [];
    queue.forEach((entry) => {
      const method = clean(entry?.method, 20);
      if (method === 'begin') beginCommerce(...(entry.args || []));
      else if (method === 'response') observeCommerceResponse(...(entry.args || []));
      else if (method === 'finish') finishCommerce(...(entry.args || []));
      else if (method === 'handoff') handoffCommerce(...(entry.args || []));
    });
  };

  const queueCommerce = (method, args) => {
    window.__ccaCommerceDiagnosticQueue = window.__ccaCommerceDiagnosticQueue || [];
    if (window.__ccaCommerceDiagnosticQueue.length < 30) {
      window.__ccaCommerceDiagnosticQueue.push({ method, args });
    }
  };

  const consentBoundCommerce = {
    begin(type, fields = {}) {
      const id = clean(fields.action_id || actionId(), 32);
      if (!consentBoundInitialized) {
        queueCommerce('begin', [type, { ...fields, action_id: id }]);
        return id;
      }
      return beginCommerce(type, { ...fields, action_id: id });
    },
    response(id, response) {
      if (!consentBoundInitialized) return queueCommerce('response', [id, response]);
      return observeCommerceResponse(id, response);
    },
    finish(id, phase, fields = {}) {
      if (!consentBoundInitialized) return queueCommerce('finish', [id, phase, fields]);
      return finishCommerce(id, phase, fields);
    },
    handoff(id, fields = {}) {
      if (!consentBoundInitialized) return queueCommerce('handoff', [id, fields]);
      return handoffCommerce(id, fields);
    },
  };

  const init = () => {
    initCommerceLifecycle();
    initSelectItem();
    initOptions();
    initHelp();
    initCartErrors();
    initCartQuantity();
    initErrors();
    initConsentBoundState();
    document.addEventListener('visitorConsentCollected', initConsentBoundState, { once: true });
    window.addEventListener('offline', () => markNetworkTransition('offline'), { passive: true });
    window.addEventListener('online', () => {
      markNetworkTransition('online');
      resumeTransports();
    }, { passive: true });
  };

  window.CCAJourneyMeasurement = {
    version: VERSION,
    state,
    commerce: consentBoundCommerce,
    get debugEvents() { return debugEvents.slice(); },
    get journeyRef() { return DEBUG ? journey() : undefined; },
    ...(DEBUG ? { classifyTechnical: (issueType, resourceType, filename, message) => ({
      issue_type: issueType,
      resource_type: resourceType,
      error_family: errorFamily(issueType, message),
      service_family: serviceFamily(filename, errorFamily(issueType, message)),
      environment_family: environmentFamily(),
    }) } : {}),
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
